import { styled, useTheme, alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../common/hooks';
import { Icon } from '../../atoms';
import { NavLink } from '../../molecules/Nav/Link';
import { NavDropdown } from '../../molecules/Nav/Dropdown';
import { isNotificationUnread, type Notification } from '../../../features/global/globalSlice';
import dayjs from 'dayjs';

const BellButtonContent = styled('span')<{ isActive: boolean }>(({ isActive = false, theme }) => ({
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    '& > svg': {
        width: '24px',
        height: '24px',
        color: isActive ? theme.palette.semantic.text.inverse : 'inherit'
    }
}));

const UnreadDot = styled('span')(({ theme }) => ({
    display: 'flex',
    width: '14px',
    height: '14px',
    padding: '2px 4px',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 'var(--Number-Scale-4s-1, 4px)',
    position: 'absolute',
    left: '0.75rem',
    borderRadius: 'var(--rounded-full, 999px)',
    border: `1px solid ${theme.palette.semantic.int.selected}`,
    background: `var(--bd-error, ${theme.palette.semantic.bd.error})`,
    top: '-0.25rem'
}));

const DropdownLegend = styled('h2')(({ theme }) => ({
    fontFamily: 'TT Commons Pro',
    fontSize: '18px',
    fontWeight: 700,
    lineHeight: '25px',
    margin: 0,
    color: theme.palette.neutral[950],
    fontStyle: 'normal'
}));

const NotificationList = styled('ul')(() => ({
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '320px',
    overflowY: 'auto',
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12
}));

const NotificationItem = styled('li')<{ isUnread?: boolean }>(({ isUnread = false, theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '12px 16px',
    background: isUnread ? theme.palette.primary[25] : theme.palette.neutral.white,
    ':not(:last-child)': {
        borderBottom: `1px solid ${theme.palette.semantic.bg.disabled}`
    },
    ':first-of-type': {
        borderTop: `1px solid ${theme.palette.semantic.bg.disabled}`
    }
}));

const NotificationDate = styled('span')(({ theme }) => ({
    color: theme.palette.semantic.text.muted,
    fontFamily: 'TT Commons Pro',
    fontSize: 12,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '17px'
}));

const SeverityTag = styled('span')<{ severity: string }>(({ severity, theme }) => {
    const colorBySeverity: Record<string, string> = {
        CRITICAL: theme.palette.berry[400],
        WARNING: theme.palette.warning[600],
        INFORMATIONAL: theme.palette.semantic.text.info
    };
    return {
        alignSelf: 'flex-start',
        fontSize: '10px',
        fontWeight: 400,
        letterSpacing: '0.04em',
        color: colorBySeverity[severity] ?? theme.palette.semantic.text.info,
        background: theme.palette.semantic.bg.info,
        borderRadius: '999px',
        padding: '0 8px'
    };
});

const NotificationTitle = styled('span')(({ theme }) => ({
    color: theme.palette.semantic.text.default,
    fontFamily: 'TT Commons Pro',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: '22px'
}));

const NotificationBody = styled('span')(({ theme }) => ({
    color: theme.palette.semantic.text.muted,
    fontFamily: 'TT Commons Pro',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '22px'
}));

const EmptyState = styled('p')(({ theme }) => ({
    fontSize: '14px',
    color: theme.palette.semantic.text.subtle,
    margin: 0,
    padding: theme.spacing(0, 3, 2)
}));

const Container = styled('div')<{ top?: number | string; right?: number | string }>(
    ({ top = 0, right = 0, theme }) => ({
        display: 'flex',
        flexDirection: 'column',
        position: 'absolute',
        top,
        right,
        width: '460px',
        maxWidth: 'calc(100vw - 32px)',
        borderRadius: 12,
        border: `1px solid ${theme.palette.neutral[200]}`,
        background: theme.palette.neutral.white,

        /* Drop Shadow - Card */
        boxShadow: `0 2px 8px 0 ${alpha(theme.palette.neutral.black, 0.05)}`
    })
);

// Override the shared NavDropdown card chrome locally instead of modifying the common component.
const StyledNavDropdown = styled(NavDropdown)({
    width: '460px',
    maxWidth: 'calc(100vw - 32px)',
    padding: 0,
    border: 'none',
    background: 'transparent',
    boxShadow: 'none',
    // Shift the dropdown ~30% of its width to the right of its anchored position.
    transform: 'translateX(30%)',
    // Hide NavDropdown's built-in close button; this dropdown has its own in the header.
    '& > button': {
        display: 'none'
    },
    top: 50
});

type Props = {
    isDropdownActive: boolean;
    onToggleDropdown(): void;
    notifications?: Notification[];
};

// Build a translation key suffix from the notification event_name, e.g. "CME update" -> "CME_UPDATE".
const toKeySuffix = (eventName?: string) =>
    (eventName ?? '')
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');

const NotificationsComponent = ({ isDropdownActive, onToggleDropdown, notifications = [] }: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const tenant = useAppSelector((state) => state?.auth?.user?.tenant);
    const latestNotificationTimestamp = useAppSelector(
        (state) => state?.auth?.user?.cwa?.[tenant]?.latestNotificationTimestamp
    );
    const isUnread = (notification: Notification) => isNotificationUnread(notification, latestNotificationTimestamp);
    const hasUnread = notifications.some(isUnread);

    // Resolve a localized title/message from the event pattern, falling back to the server-provided text.
    const getTitle = (notification: Notification) => {
        const { attributes } = notification;
        const category = attributes?.goalCategory;
        const term = t(category?.split(' ')?.[0]?.toUpperCase() || '');
        return t(`NOTIFICATION_TITLE_${toKeySuffix(attributes?.event_name)}`, {
            defaultValue: notification.title,
            term: term ? ` - ${term}` : ''
        });
    };
    const getMessage = (notification: Notification) => {
        const { attributes } = notification;
        const category = attributes?.goalCategory;
        return t(`NOTIFICATION_MESSAGE_${toKeySuffix(attributes?.event_name)}`, {
            date: getFormattedDate(notification.created_at),
            term: t(category?.split(' ')?.[0]?.toUpperCase() || '')
        });
    };
    const getSeverity = (severity: string) =>
        t(`NOTIFICATION_SEVERITY_${toKeySuffix(severity)}`, { defaultValue: severity });
    const getFormattedDate = (createdAt: string) => {
        // dayjs locale is configured globally in App.tsx (reactive to currentLanguage).
        const date = dayjs(createdAt);
        return t('NOTIFICATION_DATE_FORMAT', {
            month: date.format('MMM'),
            day: date.format('DD'),
            year: date.format('YYYY')
        });
    };

    return (
        <>
            <NavLink
                onClick={onToggleDropdown}
                id="nav-notifications-button"
                aria-expanded={isDropdownActive}
                aria-haspopup="dialog"
            >
                <BellButtonContent isActive={isDropdownActive}>
                    <Icon id="notification-bell" title={t('TEXT_NOTIFICATIONS')} />
                    {hasUnread && <UnreadDot />}
                </BellButtonContent>
            </NavLink>
            <StyledNavDropdown
                isVisible={isDropdownActive}
                parentId="nav-notifications-button"
                onClose={onToggleDropdown}
            >
                <Container role="dialog" aria-label={t('TEXT_NOTIFICATIONS')}>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '16px 24px'
                        }}
                    >
                        <DropdownLegend>{t('TEXT_NOTIFICATIONS')}</DropdownLegend>
                        <button
                            type="button"
                            onClick={onToggleDropdown}
                            aria-label={t('CLOSE_LABEL')}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                cursor: 'pointer'
                            }}
                        >
                            <Icon id="close" fill={theme.palette.semantic.int.selected} size={8} />
                        </button>
                    </div>
                    {notifications.length === 0 ? (
                        <EmptyState>{t('TEXT_NO_NOTIFICATIONS')}</EmptyState>
                    ) : (
                        <NotificationList>
                            {notifications.map((notification) => (
                                <NotificationItem key={notification.notification_id} isUnread={isUnread(notification)}>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        {isUnread(notification) && (
                                            <Icon
                                                id="list-dot"
                                                title="Unread"
                                                fill={theme.palette.semantic.int.selected}
                                                size={8}
                                            />
                                        )}
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                flexGrow: 1
                                            }}
                                        >
                                            <SeverityTag severity={notification.severity}>
                                                {getSeverity(notification.severity)}
                                            </SeverityTag>
                                            <NotificationDate>
                                                {getFormattedDate(notification.created_at)}
                                            </NotificationDate>
                                        </div>
                                    </div>

                                    <NotificationTitle>{getTitle(notification)}</NotificationTitle>
                                    {notification.message && (
                                        <NotificationBody>{getMessage(notification)}</NotificationBody>
                                    )}
                                </NotificationItem>
                            ))}
                        </NotificationList>
                    )}
                </Container>
            </StyledNavDropdown>
        </>
    );
};

export default NotificationsComponent;
